// PRO-130: the gql-only half of ./auth, split out so the BOOT PATH can import
// GET_ME / MUTATION_VERIFY_TOKEN / GET_ACTIVE_PROJECTS_DETAILS / useLogoutMutation
// from a LEAF module instead of the `src/api` barrel. The barrel drags ./auth
// (which imports `src/antd` and the routing factory) and 22 other modules onto
// the entry graph; this module imports ONLY @apollo/client + two pure gql
// fragments (plus `graphql` itself, already under @apollo/client), so it
// carries no antd/UI weight. Keep this file UI-free.
import {
  ApolloClient,
  ApolloQueryResult,
  gql,
  MutationHookOptions,
  QueryOptions,
  useMutation,
} from '@apollo/client'
import { DocumentNode, visit } from 'graphql'

import { FrgCompanySettingsPinColors } from 'src/hooks/useCompanyPinColors'

import { FrgUserForLink } from './user'

export const MUTATION_LOGOUT = gql`
  mutation Logout {
    refuseToken {
      token
    }
  }
`

export const useLogoutMutation = (options?: MutationHookOptions) =>
  useMutation(MUTATION_LOGOUT, options)

export const MUTATION_LOGIN = gql`
  mutation Login($username: String!, $password: String!) {
    tokenAuth(password: $password, username: $username) {
      token
      # mfa-email-login (S3): a company with mfa_email_enabled and a
      # non-driver persona gets a challenge instead of a session — token is
      # null and these carry the code step. Contract-first fields (declared
      # in schema.local-extensions.graphql until the MFA backend deploys).
      mfaRequired
      mfaChallengeId
      mfaCodeTtlSeconds
    }
  }
`

// mfa-email-login (S3): the code step. verifyMfaCode exchanges the emailed
// code for the JWT (the token path then runs exactly as a password login);
// resendMfaCode re-sends and returns a neutral status.
export const MUTATION_MFA_VERIFY = gql`
  mutation VerifyMfaCode($challengeId: String!, $code: String!) {
    verifyMfaCode(challengeId: $challengeId, code: $code) {
      token
    }
  }
`

export const MUTATION_MFA_RESEND = gql`
  mutation ResendMfaCode($challengeId: String!) {
    resendMfaCode(challengeId: $challengeId) {
      status
    }
  }
`

export const FrgActiveProject = gql`
  fragment FrgActiveProject on ProjectType {
    id
    name
    userAccessLevel
    logo
    company {
      id
      name
      dashboardLocation {
        id
        latitude
        longitude
        placeId
        name
      }
    }
    containerTypes {
      edges {
        node {
          id
          name
          length
          width
          height
          diameter
          isCylindrical
          volume
        }
      }
    }
    measurementsettingsSet {
      edges {
        node {
          id
        }
      }
    }
    wasteFractions {
      edges {
        node {
          id
          name
          wasteCategory
          wasteSubstance
          weightFactor
        }
      }
    }
  }
`

export const GET_ME = gql`
  ${FrgActiveProject}
  ${FrgUserForLink}
  ${FrgCompanySettingsPinColors}
  query GetMeByProjectIds($projectList: [ID]) {
    me {
      ...FrgUserForLink
      isStaff
      isAdmin
      isSuperuser
      isDemo
      isReseller
      isMaster
      isBeta
      # NEXUS-734: setRoles derives the customer-registry role from this —
      # a pure driver must not see the customer registry (SCP) surfaces.
      isDriver
      isHaulerUser
      hauler {
        id
        name
      }
      userId
      email
      intercomUserHash
      createdAt
      phoneNumber
      logo
      datetimeFormatLocale
      activeProjectsIds
      daysBeforeDemoExpires
      language
      themePreference
      ownProjectsIds
      brokerType
      ownProjects {
        edges {
          node {
            id
            name
          }
        }
      }
      jobtitle {
        id
        title
      }
      companyRole {
        id
        name
        permissions
      }
      # PRO-945: every role the user holds. Contract-first field
      # (schema.local-extensions.graphql until BE #2345 deploys); listed in
      # GET_ME_CONTRACT_FIRST_FIELDS below so an older backend makes queryMe
      # retry without it. Read only by the container-washing foreman check,
      # which fails closed when it is absent.
      companyRoles {
        id
        name
      }
      permissions
      activeProjects(projectsIds: $projectList) {
        edges {
          node {
            ...FrgActiveProject
          }
        }
      }
      company {
        id
        logo
        name
        isActive
        companySettings {
          id
          mapCountry
          # PRO-457: company-wide toggle read by the address formatter
          # (useAddressWithCountry) so every address surface can show the
          # country. Contract-first field (schema.local-extensions.graphql)
          # until the BE branch pro-457-country-in-address-be deploys
          # CompanySettings.show_country_in_address.
          showCountryInAddress
          metabaseEnabled
          invoicingEnabled
          # "Billing handled externally" (Visma & co): keeps the invoicing
          # module and the invoice-related customer fields VISIBLE but
          # read-only. Contract-first field (declared in
          # schema.local-extensions.graphql until BE branch
          # feat/external-billing-readonly deploys
          # CompanySettings.external_billing_enabled). Fails closed-as-OFF in
          # src/hooks/useExternalBillingEnabled — an absent value must never
          # lock a normal tenant out of their own billing.
          externalBillingEnabled
          # The two accounting registers inside the invoicing module, one tab
          # each: OFF (the default) means this tenant gets the simplest
          # invoicing — invoices out the door, no chart of accounts — with the
          # Accounting accounts / Routing rules tabs and their routes hidden.
          # Contract-first fields (declared in schema.local-extensions.graphql
          # until BE branch feat/invoicing-tab-flags deploys
          # CompanySettings.invoicing_accounting_accounts_enabled /
          # invoicing_routing_rules_enabled). Fail closed-as-OFF in
          # src/reducers/routing/utils.ts (the two tab roles) — hiding a tab is
          # the safe direction, and stored accounts/rules keep routing charges
          # either way.
          invoicingAccountingAccountsEnabled
          invoicingRoutingRulesEnabled
          insightsEnabled
          productPricingEnabled
          # Staff flag for the per-agreement price coefficient (HRA factor
          # fees): shows the coefficient inputs on the agreement forms and
          # the x{coefficient} display surfaces. Contract-first field
          # (schema.local-extensions.graphql until the agreement-coefficient
          # BE deploys CompanySettings.agreement_coefficient_enabled). Fails
          # closed in src/hooks/useAgreementCoefficientEnabled.
          agreementCoefficientEnabled
          # Retroactive corrections (BRIDGE-9): per-company switch behind the
          # "Effective from" date on the agreement edit / end flows. Fails
          # closed in src/hooks/useRetroactiveCorrectionEnabled, so tenants
          # that never bill through Visma never see the field.
          retroactiveCorrectionEnabled
          # Staff flag "Service responsibilities & Industries": force-enables
          # the FI CRM catalogue tabs — and the contact/company forms' FI
          # fields (PRO-511) — for any company. Now served by the deployed BE
          # (CompanySettings.fi_catalogues_enabled).
          fiCataloguesEnabled
          # PRO-511 / NEXUS-773: staff flag showing the "Receives marketing
          # communications" consent field on the Individual/Company forms.
          # Contract-first field (declared in schema.local-extensions.graphql
          # until BE branch feat/pro511-nexus-field-flags deploys
          # CompanySettings.marketing_consent_enabled).
          marketingConsentEnabled
          # PRO-511 / NEXUS-770: staff flag showing the Finnish statutory
          # non-disclosure (turvakielto) fields on contact forms; the
          # contact_ndr__* permission gates still apply on top (flag AND
          # permission). Contract-first field (schema.local-extensions.graphql
          # until the same BE branch deploys CompanySettings.ndr_enabled).
          ndrEnabled
          wastewaterTreatmentEnabled
          # HSY tender flags (HSY-243 / 245). Both default OFF — each gates a
          # surface on a screen every tenant already uses, so they stay
          # invisible until staff enable them per company. (HSY-254's
          # portalProductCatalogueEnabled is gone: the citizen portal prices
          # from Products & Prices behind the BE-only portal_product_pricing
          # flag and never read the catalogue field.)
          priceListExportEnabled
          productWorkTasksEnabled
          # HSY tender batch (2026-09): one staff-set, default-OFF switch per
          # requirement (234, 243+254, 244, 251, 256, 258, 263). Contract-first
          # fields (schema.local-extensions.graphql until the BE
          # feat/hsy-tenant-flags deploys migration 0354); each fails closed
          # in src/hooks/useHsyFlags, so OFF = today's UI for every tenant.
          priceAmendmentsEnabled
          productChannelControlsEnabled
          specialPriceTermsEnabled
          contractAreaTransferEnabled
          ticketAreaDeadlinesEnabled
          holidayCancelPolicyEnabled
          reliabilityExclusionWindowsEnabled
          # HSY tender 70 + 182: two more staff-set, default-OFF switches,
          # each with the one tenant-editable setting its feature needs.
          # scpShareSumTolerancePct is a Decimal (a string on the wire) and
          # containerWashingApproverRole is a CompanyRoleDef — both are read
          # ONLY through src/hooks/useHsyFlags, which reports "no margin" /
          # "nobody nominated" while the owning flag is off, so a value the
          # BE serves can never loosen the exact-100 share rule or hand out
          # a wash-list approval right on a tenant without the feature.
          # Contract-first fields (schema.local-extensions.graphql until BE
          # feat/hsy70-182-flags deploys migration 0355).
          scpShareSumToleranceEnabled
          scpShareSumTolerancePct
          containerWashingEnabled
          containerWashingApproverRole {
            id
            name
          }
          # HSY-235 (COMPASS-1687): four-decimal prices — the staff-set
          # switch plus the tenant's unit-price precision (2 or 4). Contract-
          # first fields (schema.local-extensions.graphql until the BE deploys
          # migrations core/0355 + product/0064); fail closed (OFF / 2 dp) in
          # src/hooks/useHsyFlags + src/utils/priceDecimals. Totals stay 2 dp.
          fourDecimalPricesEnabled
          priceDecimalPlaces
          # HSY-253 (COMPASS-1698): the correct-to-undone form with the
          # service-provider hand-over replaces the plain stop reset while ON.
          # Contract-first field (schema.local-extensions.graphql until BE
          # #2136 deploys CompanySettings.task_correction_handover_enabled).
          # Listed in GET_ME_CONTRACT_FIRST_FIELDS below, so a backend that
          # predates it makes queryMe retry without the field instead of
          # failing the boot; the hook then reads it as OFF
          # (src/hooks/useHsyFlags.ts).
          taskCorrectionHandoverEnabled
          # HSY-28/206 (COMPASS-1187/1197): truckload (kuorma) tracking.
          # Contract-first field (schema.local-extensions.graphql until the
          # BE deploys core/0361); fails closed in src/hooks/useHsyFlags.
          truckloadsEnabled
          # HSY req 86 §5.4 (COMPASS-1773): agreement suspension (keskeytys).
          # Contract-first field (schema.local-extensions.graphql until the
          # COMPASS-1773 backend deploys); listed in
          # GET_ME_CONTRACT_FIRST_FIELDS below so an older backend makes
          # queryMe retry without it; fails closed in src/hooks/useHsyFlags.
          agreementSuspensionEnabled
          ticketVisibilityScopeEnabled
          liveOperationsEnabled
          haulerModuleEnabled
          haulerB2bEnabled
          legacyBillingRuns
          legacyDataSync
          legacyPricing
          # Omnisearch rollout gate: switches the office global search between
          # the legacy Search modal (off) and the ⌘K omnisearch palette (on).
          # Contract-first field (declared in schema.local-extensions.graphql
          # until BE branch feat/omnisearch-company-flag deploys
          # CompanySettings.omnisearch_enabled). Fails closed in
          # src/hooks/useOmnisearchEnabled.
          omnisearchEnabled
          # Self Composting service rollout gate: hides every composting
          # surface (register tabs, the settings form-builder entry) while
          # off; the BE also 404s the citizen portal composting API.
          # Contract-first field (declared in schema.local-extensions.graphql
          # until BE branch feat/composting-optional-contact deploys
          # CompanySettings.crm_enable_composting). Fails closed in
          # src/hooks/useCompostingEnabled and the availability predicate.
          crmEnableComposting
          # PRO-143 follow-up: put the waste-fraction NAME back beside its icon
          # in office list cells for a tenant that reads its lists by name
          # (legacy pricing = one fraction per product). Contract-first field
          # (declared in schema.local-extensions.graphql until BE branch
          # feat/waste-fraction-labels-flag deploys
          # CompanySettings.waste_fraction_labels_enabled). Fails closed in
          # tenantFeature('wasteFractionLabels') = today's icon-only cells.
          wasteFractionLabelsEnabled
          legacyContactsEnabled
          standaloneContactsEnabled
          # PRO-522: the whole Shared Services module (was the FE hostname flag
          # tenantFeature('sharedServices')) + its Finland statutory sub-gate.
          # Contract-first fields (declared in schema.local-extensions.graphql
          # until the PRO-522 BE deploys CompanySettings.shared_services_enabled
          # / shared_services_finland_enabled). Read in src/config/tenant.ts.
          sharedServicesEnabled
          sharedServicesFinlandEnabled
          haulerComplianceCountry
          routeStudioEnabled
          physicalKeysEnabled
          inventoryEnabled
          # COMPASS-1190: per-company Map Planning workspace gate, read by
          # src/hooks/useMapPlanningEnabled (the single source for the
          # sidebar entry, the quick-navigation directory AND the
          # /app/map-planning route gate; fails closed while absent).
          # Contract-first field (declared in schema.local-extensions.graphql
          # until BE #2103 deploys CompanySettings.map_planning_enabled) —
          # and, unlike the earlier contract-first fields above, listed in
          # GET_ME_CONTRACT_FIRST_FIELDS below: a backend that predates it
          # refuses the WHOLE document, so queryMe retries without it
          # instead of leaving every signed-in user on the boot skeleton.
          mapPlanningEnabled
          # PRODUCT-513: the per-company skip-hire module gate, read by
          # src/hooks/useSkipHireEnabled — the single source for every
          # skip-hire surface the skiphire branch built (Settings tiles and
          # routes, the Rentals / Permits tabs, the Rent-container actions,
          # the rental order form, the ticket-page cards, …); fails closed
          # while absent so a company without it gets trunk's office
          # byte-for-byte. Contract-first field (declared in
          # schema.local-extensions.graphql until the skiphire backend
          # deploys CompanySettings.skip_hire_enabled, catalog key
          # module.skip_hire) and listed in GET_ME_CONTRACT_FIRST_FIELDS
          # below, so a backend that predates it refuses only this field
          # and queryMe retries without it instead of stranding every
          # signed-in user on the boot skeleton.
          skipHireEnabled
          # COMPASS-1765: the per-company Fixed routes module gate, read by
          # src/hooks/useFixedRoutesEnabled — the single source for the Fixed
          # route template tab on the route detail page. Contract-first
          # (declared in schema.local-extensions.graphql until the backend
          # deploys CompanySettings.fixed_routes_enabled, catalog key
          # module.fixed_routes) and listed in GET_ME_CONTRACT_FIRST_FIELDS
          # below, so a backend that predates it refuses only this field.
          # Fails closed while absent: no tab, and none of the template
          # selections ever reach a backend that would refuse them.
          fixedRoutesEnabled
          # COMPASS-1765: the "apply to many routes" switch under the module
          # (Apply to upcoming sub-tab, routes-list bulk Apply). Read by
          # src/hooks/useFixedRoutesEnabled's useFixedRoutesBulkApplyEnabled;
          # contract-first and fail-closed like fixedRoutesEnabled.
          fixedRoutesBulkApplyEnabled
          ssoEmailDomains
          ...FrgCompanySettingsPinColors
        }
        settings {
          clockFormat12
          currency
        }
        dashboardLocation {
          id
          latitude
          longitude
          placeId
          name
        }
        projectSet {
          edges {
            node {
              id
            }
          }
        }
      }
      userLayoutConfig {
        id
        driverOrder
        driverOrderBy
        driverPageSize
        driverIconsSize
        driverDisabledColumns
        locationsOrder
        locationsOrderBy
        locationsPageSize
        locationsIconsSize
        locationsDisabledColumns
        vehicleOrder
        vehicleOrderBy
        vehiclePageSize
        vehicleIconsSize
        vehicleDisabledColumns
        vehicleTypesOrder
        vehicleTypesOrderBy
        vehicleTypesPageSize
        vehicleTypesDisabledColumns
        routeOrder
        routeOrderBy
        routePageSize
        routeDisabledColumns
        inquiryOrder
        inquiryOrderBy
        inquiryPageSize
        inquiryDisabledColumns
        containersOrder
        containersOrderBy
        containersPageSize
        containersDisabledColumns
        collectionContainersOrder
        collectionContainersOrderBy
        collectionContainersPageSize
        collectionContainersDisabledColumns
        pickupOrdersOrder
        pickupOrdersOrderBy
        pickupOrdersPageSize
        pickupOrdersDisabledColumns
        notificationsOrder
        notificationsOrderBy
        notificationsPageSize
        notificationsDisabledColumns
        analyticsCalculationType
        analyticsPreviousPeriod
        analyticsCustomPeriodFrom
        analyticsCustomPeriodTo
        analyticsCustomPeriodComparisonFrom
        analyticsCustomPeriodComparisonTo
        analyticsHistoricalPeriod
      }
    }
  }
`

/**
 * GET_ME selections this frontend ships AHEAD of the backend ("contract-first":
 * declared in schema.local-extensions.graphql so codegen knows them, real on
 * the wire only once the BE deploys). A GraphQL server does not answer an
 * unknown field with null — it refuses the WHOLE document at validation
 * (`Cannot query field …`, HTTP 400 from graphene-django), so a `me` that
 * names one field the connected backend predates fails outright: the boot's
 * `getUser` (src/App/router.tsx) catches, `setUser` never runs, `init` never
 * flips, and every signed-in user sits on the boot skeleton — the same for
 * the password login (./auth) and the SSO callback. `queryMe` turns that into
 * ONE retry without the refused fields, so they read as absent and each
 * consumer's documented fail-safe (OFF) holds. Only names listed here are
 * ever dropped: a refusal naming anything else is real schema drift and is
 * rethrown, loudly, as today. Remove a name once its BE is deployed
 * everywhere this build ships.
 */
export const GET_ME_CONTRACT_FIRST_FIELDS: ReadonlySet<string> = new Set([
  // COMPASS-1190 (BE #2103): CompanySettings.map_planning_enabled
  'mapPlanningEnabled',
  // COMPASS-1698 (BE #2136): CompanySettings.task_correction_handover_enabled
  'taskCorrectionHandoverEnabled',
  // PRODUCT-513 (skiphire backend): CompanySettings.skip_hire_enabled — the
  // skip-hire module flag; absent on every backend until the skiphire BE
  // deploys, and absent on the QA backend today.
  'skipHireEnabled',
  // BRIDGE-9 (BE #2239): CompanySettings.retroactive_correction_enabled —
  // contract-first until that backend deploys; without this entry an older
  // backend refuses the whole me document and every sign-in stalls.
  'retroactiveCorrectionEnabled',
  // COMPASS-1765 (fixed-routes backend): CompanySettings.fixed_routes_enabled
  // — the Fixed routes module flag; absent on every backend until that BE
  // deploys.
  'fixedRoutesEnabled',
  // COMPASS-1765: CompanySettings.fixed_routes_bulk_apply_enabled - the
  // apply-to-many-routes switch under the module; absent until that BE
  // deploys.
  'fixedRoutesBulkApplyEnabled',
  // COMPASS-1773 (BE PR "COMPASS-1773"): CompanySettings
  // .agreement_suspension_enabled — contract-first until that backend
  // deploys.
  'agreementSuspensionEnabled',
  // PRO-945 (BE #2345): UserType.company_roles — every role the user holds;
  // contract-first until that backend deploys.
  'companyRoles',
])

/** `document` without every field selection named in `names`, at any depth. */
export const withoutFields = (
  document: DocumentNode,
  names: ReadonlySet<string>
): DocumentNode =>
  visit(document, {
    Field: (node) => (names.has(node.name.value) ? null : undefined),
  })

/**
 * `Cannot query field "X" on type "Y".` — graphql-core 2 quotes the name with
 * ", graphql-core 3 with '. Validation reports EVERY unknown field of the
 * document in one answer, so one pass collects them all.
 */
const UNKNOWN_FIELD = /Cannot query field ["']([^"']+)["']/g

/**
 * Every place a GraphQL error message can hide in an ApolloError: the live
 * graph-api answers a document-validation failure with HTTP 400 + a JSON
 * `errors` body, which Apollo surfaces as a ServerError on `networkError`
 * (parsed body on `.result`) with EMPTY `graphQLErrors`; a 200-with-errors
 * server fills `graphQLErrors` instead. (The shape the settlement, manual-
 * entries and reconciliation recognisers check too.)
 */
const errorMessages = (error: unknown): string[] => {
  if (!error || typeof error !== 'object') return []
  const apolloError = error as {
    message?: unknown
    graphQLErrors?: ReadonlyArray<{ message?: unknown } | null>
    networkError?: { result?: unknown } | null
  }
  const messages: string[] = []
  if (typeof apolloError.message === 'string')
    messages.push(apolloError.message)
  ;(apolloError.graphQLErrors ?? []).forEach((e) => {
    if (typeof e?.message === 'string') messages.push(e.message)
  })
  const result =
    apolloError.networkError && 'result' in apolloError.networkError
      ? apolloError.networkError.result
      : undefined
  if (typeof result === 'string') {
    messages.push(result)
  } else if (result && typeof result === 'object') {
    const { errors } = result as {
      errors?: ReadonlyArray<{ message?: unknown } | null>
    }
    ;(errors ?? []).forEach((e) => {
      if (typeof e?.message === 'string') messages.push(e.message)
    })
  }
  return messages
}

/** The field names a document-validation refusal names (deduplicated). */
export const unknownFieldNames = (error: unknown): string[] => {
  const names = new Set<string>()
  errorMessages(error).forEach((message) => {
    Array.from(message.matchAll(UNKNOWN_FIELD)).forEach((match) =>
      names.add(match[1])
    )
  })
  return [...names]
}

type TMeClient = Pick<ApolloClient<object>, 'query'>

/**
 * `client.query({ query: GET_ME })` that survives a backend predating the
 * contract-first fields (see GET_ME_CONTRACT_FIRST_FIELDS): when the server
 * refuses the document naming only such fields, the query is re-sent once
 * without them. Every other failure — and a refusal naming a field NOT
 * listed — rejects exactly as the plain query does. Used by the three places
 * that load `me` into `userVar`: the boot (src/App/router.tsx), the password
 * login (./auth) and the SSO callback.
 */
export const queryMe = async (
  client: TMeClient,
  options: Omit<QueryOptions, 'query'> = {}
): Promise<ApolloQueryResult<$TSFixMe>> => {
  try {
    return await client.query({ ...options, query: GET_ME })
  } catch (error) {
    const refused = unknownFieldNames(error)
    if (
      refused.length === 0 ||
      refused.some((name) => !GET_ME_CONTRACT_FIRST_FIELDS.has(name))
    ) {
      throw error
    }
    // eslint-disable-next-line no-console
    console.warn(
      `GetMe: the backend does not serve ${refused.join(
        ', '
      )} yet; retrying without`
    )
    return client.query({
      ...options,
      query: withoutFields(GET_ME, new Set(refused)),
    })
  }
}

export const GET_ACTIVE_PROJECTS_DETAILS = gql`
  ${FrgActiveProject}
  query GetActiveProjectsDetails($ids: [ID]) {
    allProjects(ids: $ids) {
      edges {
        node {
          ...FrgActiveProject
        }
      }
    }
  }
`

export const MUTATION_VERIFY_TOKEN = gql`
  mutation VerifyToken($token: String!) {
    verifyToken(token: $token) {
      payload {
        username
        exp
        origIat
      }
      user {
        username
        lastName
        firstName
      }
    }
  }
`
